/**
 * Per-app mount overrides.
 *
 * Apps do not all mount uniformly, and that's the whole reason this file
 * exists. tracking-profiles lives inside the existing OpportunitiesLayout
 * route (/opportunities/tracking-profiles) rather than at the top level; a
 * future /admin app will need to mount inside a RequireAdminRoute guard.
 * Neither fact is derivable from the app package list — appRegistry only
 * knows an app's chrome and where its chunk lives — so it's named here, per
 * app id.
 *
 * An app absent from this map mounts as a plain top-level route. That's the
 * common case: keep this map small, and only add an entry when an app's
 * placement in router.tsx can't be inferred from the app package itself.
 *
 * .tsx, not .ts, even though nothing here needs JSX today — so that a future
 * `wrap` is an edit, not a file rename.
 */ export const mountOverrides = {
    'tracking-profiles': {
        parentPath: 'opportunities'
    }
};
/**
 * Runtime backstop for the two ways this map silently drifts from the route
 * tree it's supposed to describe. AppId (registry.gen.ts) already closes key
 * drift at compile time from a regex-parsed manifest literal — this re-checks
 * it at runtime because a regex is not a type checker — and it closes parent
 * drift, which nothing at compile time can see: `parentPath` is a free string
 * compared against another free string at each `appRoutes(...)` call site in
 * router.tsx, so a typo in either one just mounts (or drops) an app with no
 * error. Called once at router module init, against what appRoutes actually
 * emitted, so a drift fails the moment the module loads instead of showing up
 * as a vanished nav item.
 */ export function assertMountTableConsistent(registryEntries, overrides, mountedAppIds) {
    const registryIds = new Set(registryEntries.map((entry)=>entry.id));
    for (const key of Object.keys(overrides)){
        if (!registryIds.has(key)) {
            throw new Error(`mountOverrides: key '${key}' matches no app in the registry — it's either a stale ` + 'app id (renamed or removed) or a typo. Fix the key, or remove the entry if the app is gone.');
        }
    }
    const mountCounts = new Map();
    for (const id of mountedAppIds){
        mountCounts.set(id, (mountCounts.get(id) ?? 0) + 1);
    }
    for (const id of registryIds){
        const count = mountCounts.get(id) ?? 0;
        const parentPath = overrides[id]?.parentPath;
        if (count === 0) {
            throw new Error(`mountOverrides: app '${id}' declares parentPath ` + `${parentPath ? `'${parentPath}'` : '(top level)'}, but no appRoutes(...) call site ` + 'in router.tsx matches it — the app is missing from the route tree entirely.');
        }
        if (count > 1) {
            throw new Error(`mountOverrides: app '${id}' was mounted by ${count} appRoutes(...) call sites — it ` + 'must match exactly one.');
        }
    }
}
